import { createFileRoute, notFound } from "@tanstack/react-router";
import { cityFromSlug, specialtyFromSlug } from "@/lib/seo-slugs";
import { getIntentProviders } from "@/lib/seo-intent.functions";
import { decideIndexability, robotsMeta } from "@/lib/seo-indexability";
import { IntentPageShell } from "@/components/seo/IntentPageShell";
import { setSeoPageCache } from "@/lib/page-cache";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/specialty/$specialty/$city/accepting-new-patients")({
 staleTime: 10 * 60_000,
 loader: async ({ params }) => {
 await setSeoPageCache();
 const specialty = specialtyFromSlug(params.specialty);
 const city = cityFromSlug(params.city);
 if (!specialty || !city) throw notFound();
 const cityName = city.split(",")[0].trim();
 const state = (city.split(",")[1] ?? "").trim();
 const { providers, total } = await getIntentProviders({
 data: { specialty, city: cityName, state: state || undefined, kind: "accepting_new_patients" },
 });
 return { specialty, city, cityName, providers, total };
 },
 head: ({ params, loaderData }) => {
 const specialty = loaderData?.specialty ?? "Providers";
 const city = loaderData?.city ?? "";
 const total = loaderData?.total ?? 0;
 const url = `${SITE}/specialty/${params.specialty}/${params.city}/accepting-new-patients`;
 const baseUrl = `${SITE}/specialty/${params.specialty}/${params.city}`;
 const title = `${specialty} accepting new patients in ${city} | Heallexa`;
 const description = `${specialty} actively accepting new patients in ${city}. Compare verified providers, check insurance accepted, read reviews, and request an appointment — free, no signup, on Heallexa.`;
 const robots = decideIndexability({ providerCount: total, hasRelatedProviders: total > 0, internalLinkCount: 1 });
 const canonical = robots.index ? url : baseUrl;
 return {
 meta: [
 { title }, { name: "description", content: description },
 { property: "og:title", content: title }, { property: "og:description", content: description },
 { property: "og:url", content: canonical }, robotsMeta(robots.robots),
 ],
 links: [{ rel: "canonical", href: canonical }],
 };
 },
 component: () => {
 const { specialty, city, cityName, providers } = Route.useLoaderData();
 return (
 <IntentPageShell
 title={`${specialty} accepting new patients in ${city}`}
 description=""
 h1={`${specialty} accepting new patients in ${cityName}`}
 intro={`${specialty} in ${city} who are currently accepting new patient appointments on Heallexa.`}
 emptyMessage={`No ${specialty.toLowerCase()} in ${city} have flagged "accepting new patients" yet. Try the full ${specialty.toLowerCase()} directory.`}
 providers={providers}
 breadcrumbs={[
 { label: "Heallexa", href: "/" },
 { label: specialty },
 { label: cityName },
 { label: "Accepting new patients" },
 ]}
 />
 );
 },
});
